import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { PageHeader } from '../components/PageHeader';
import { Banner } from '../components/Banner';
import { SessionResults } from '../components/SessionResults';
import {
  clockTime,
  dayDate,
  dayKey,
  dayLabel,
  SLOT_NAMES,
  type SessionReport,
  type SessionView,
} from '../lib/sessions';

/** How far back the dashboard looks before anybody asks it to look further. */
const WEEKS_BACK = 8;

/**
 * The analytics dashboard: what each test day produced.
 *
 * A centre reads its results by session rather than by paper — "how did
 * Saturday morning go?" — because a session is the thing it ran. So this opens
 * on the most recent one that has marks in it, and the rest of the term is one
 * click away.
 */
export function AnalyticsPage() {
  const [sessions, setSessions] = useState<SessionView[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [report, setReport] = useState<SessionReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const window = useMemo(() => {
    const today = new Date();
    const back = new Date(today.getFullYear(), today.getMonth(), today.getDate() - WEEKS_BACK * 7);
    const forward = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 7);
    return { from: dayKey(back), to: dayKey(forward) };
  }, []);

  useEffect(() => {
    api
      .get<{ sessions: SessionView[] }>('/sessions', { params: window })
      .then(({ data }) => {
        // Newest first: a dashboard is asked about the session that just ran.
        const ran = [...data.sessions]
          .filter((session) => session.candidates.length > 0)
          .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
        setSessions(ran);
        setOpenId((current) => current ?? ran[0]?.id ?? null);
        setError(null);
      })
      .catch((err) => setError(errorMessage(err, 'Could not load the sessions')))
      .finally(() => setLoading(false));
  }, [window]);

  useEffect(() => {
    if (!openId) return;
    setReport(null);
    api
      .get<SessionReport>(`/sessions/${openId}/results`)
      .then(({ data }) => setReport(data))
      .catch((err) => setError(errorMessage(err, 'Could not load those results')));
  }, [openId]);

  const label = (session: SessionView) =>
    session.name || `${SLOT_NAMES[session.slot]}, ${dayLabel(session.day)}`;

  return (
    <>
      <PageHeader
        title="Analytics"
        meta={
          <p className="muted">
            <span className="mono">{sessions.length}</span>{' '}
            {sessions.length === 1 ? 'session' : 'sessions'} in the last{' '}
            <span className="mono">{WEEKS_BACK}</span> weeks
          </p>
        }
        actions={
          <Link className="btn" to="/schedule">
            Timetable
          </Link>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : sessions.length === 0 ? (
          <div className="empty">
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--step-1)' }}>
              No test days yet
            </p>
            <p className="muted" style={{ maxWidth: '52ch', margin: '0 auto' }}>
              Put a session in the timetable, choose its papers and who is sitting it, and what it
              produced appears here — best band first, and downloadable.
            </p>
            <Link className="btn btn--primary" to="/schedule">
              Open the timetable
            </Link>
          </div>
        ) : (
          <>
            <div className="listbar">
              <div className="groupbar__chips">
                <span className="eyebrow">Session</span>
                {sessions.slice(0, 12).map((session) => (
                  <button
                    key={session.id}
                    type="button"
                    className={`chip ${openId === session.id ? 'chip--on' : ''}`}
                    onClick={() => setOpenId(session.id)}
                  >
                    {label(session)}
                  </button>
                ))}
              </div>
            </div>

            {report ? (
              <>
                <p className="field__hint">
                  <Link to={`/schedule/${report.session.id}`}>
                    {label(report.session)}
                  </Link>{' '}
                  · {dayLabel(report.session.day)} {dayDate(report.session.day)} ·{' '}
                  {clockTime(report.session.startsAt)}–{clockTime(report.session.endsAt)}
                  {report.session.shuffle ? ' · papers dealt around the room' : ''}
                </p>
                <SessionResults
                  report={report}
                  heading={label(report.session)}
                  downloadPath={`/sessions/${report.session.id}/results/export`}
                />
              </>
            ) : (
              <p className="muted">Loading the results…</p>
            )}
          </>
        )}
      </div>
    </>
  );
}
